import { inject, Injectable, signal } from '@angular/core';
import { ApiService } from './api.service';
import { IKeywordDetails } from '../interfaces/i-keyword';
import { AlertService } from '../shared/alert/alert.service';
import { AlertEnum } from '../shared/alert/alert.enum';

@Injectable({
  providedIn: 'root'
})
export class KeywordService {
  // Signal to hold all available keywords
  keywords = signal<IKeywordDetails[]>([]);

  // Signal to track loading state
  isLoading = signal<boolean>(false);

  // Signal to track if keywords have been loaded at least once
  isLoaded = signal<boolean>(false);

  #apiService = inject(ApiService);
  #alertService = inject(AlertService);

  /**
   * Loads all keywords from the backend API.
   * Uses cached keywords if already loaded, unless forceRefresh is true.
   * Safe to call from multiple components simultaneously - only makes one API call.
   * @param forceRefresh - If true, fetches fresh data from the API even if already loaded
   */
  loadKeywords(forceRefresh = false): void {
    // If already loaded and not forcing refresh, skip
    if (this.isLoaded() && !forceRefresh) {
      return;
    }

    // If already loading, don't start another request (prevents race condition)
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);

    this.#apiService.getAllKeywords().subscribe({
      next: (response) => {
        this.keywords.set(response.keywords);
        this.isLoaded.set(true);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load keywords', error);
        this.#alertService.showAlert({
          message: 'Failed to load keywords.',
          type: AlertEnum.error
        });
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Gets a keyword by its ID.
   * @param id - The keyword ID to find
   * @returns The keyword if found, undefined otherwise
   */
  getKeywordById(id: string): IKeywordDetails | undefined {
    return this.keywords().find(k => k.id === id);
  }

  /**
   * Gets keywords by their IDs.
   * @param ids - Array of keyword IDs to find
   * @returns Array of found keywords
   */
  getKeywordsByIds(ids: string[]): IKeywordDetails[] {
    return this.keywords().filter(k => ids.includes(k.id));
  }

  /**
   * Converts an array of keyword IDs to their full keyword objects.
   * Useful when migrating from string[] to IKeywordDetails[].
   * @param ids - Array of keyword IDs
   * @returns Array of keyword objects
   */
  convertIdsToKeywords(ids: string[]): IKeywordDetails[] {
    return this.getKeywordsByIds(ids);
  }

  /**
   * Filters keywords by type.
   * @param type - The type to filter by
   * @returns Array of keywords matching the type
   */
  getKeywordsByType(type: string): IKeywordDetails[] {
    return this.keywords().filter(k => k.type === type);
  }

  /**
   * Filters keywords by category.
   * @param category - The category to filter by
   * @returns Array of keywords matching the category
   */
  getKeywordsByCategory(category: string): IKeywordDetails[] {
    return this.keywords().filter(k => k.category === category);
  }

  /**
   * Searches keywords by text (searches title, label, type, and searchText).
   * @param searchTerm - The search term
   * @returns Array of keywords matching the search term
   */
  searchKeywords(searchTerm: string): IKeywordDetails[] {
    const term = searchTerm.toLowerCase();
    return this.keywords().filter(k =>
      k.title.toLowerCase().includes(term) ||
      k.label?.toLowerCase().includes(term) ||
      k.type.toLowerCase().includes(term) ||
      k.typeDescription?.toLowerCase().includes(term) ||
      k.category?.toLowerCase().includes(term) ||
      k.searchText?.toLowerCase().includes(term)
    );
  }

  /**
   * Gets only enabled keywords.
   * @returns Array of enabled keywords
   */
  getEnabledKeywords(): IKeywordDetails[] {
    return this.keywords().filter(k => k.isEnabled);
  }

  /**
   * Merges API keywords with legacy/hardcoded keyword strings.
   * Creates temporary IKeywordDetails objects for legacy strings that don't exist in API keywords.
   * Avoids duplicates by checking title (case-insensitive).
   * @param legacyStrings - Array of legacy keyword strings to merge
   * @returns Combined array of IKeywordDetails objects
   */
  mergeWithLegacyStrings(legacyStrings: string[]): IKeywordDetails[] {
    const apiKeywords = this.keywords();

    // Create temporary IKeywordDetails objects for legacy strings
    const legacyAsKeywords: IKeywordDetails[] = legacyStrings.map(title => ({
      id: `legacy-${title}`,
      title,
      type: 'Legacy',
      typeDescription: 'Legacy hardcoded keyword',
      isEnabled: true
    }));

    // Merge, avoiding duplicates by title (case-insensitive)
    const combined = [...apiKeywords];
    legacyAsKeywords.forEach(legacy => {
      const isDuplicate = combined.some(
        k => k.title.toLowerCase() === legacy.title.toLowerCase()
      );
      if (!isDuplicate) {
        combined.push(legacy);
      }
    });

    return combined;
  }
}
